import { useEffect, useState } from 'react';
import { CheckCircle, X, FileText, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AutoCaseNotificationProps {
    caseId?: number;
    caseName?: string;
    address?: string;
    riskScore?: number;
    onClose?: () => void;
}

export function AutoCaseNotification({ caseId, caseName, address, riskScore, onClose }: AutoCaseNotificationProps) {
    const [isVisible, setIsVisible] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Auto-close after 8 seconds
        const timer = setTimeout(() => {
            handleClose();
        }, 8000);

        return () => clearTimeout(timer);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            onClose?.();
        }, 300); // Wait for fade-out animation
    };

    const handleViewCase = () => {
        if (caseId) {
            navigate(`/cases/${caseId}`);
            handleClose();
        }
    };

    if (!isVisible) return null;

    return (
        <div className={`fixed top-4 right-4 z-50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="bg-card border border-border rounded-lg max-w-md overflow-hidden shadow-overlay">
                {/* Header */}
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-400">High risk</span>
                        <span className="font-semibold">Address Detected</span>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-muted-foreground hover:text-foreground hover:bg-muted transition-colors rounded p-0.5"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4">
                    <p className="text-foreground mb-3">
                        A case has been automatically created for this high-risk address:
                    </p>

                    {/* Address Badge */}
                    {address && (
                        <div className="bg-background rounded-lg px-3 py-2 mb-3 border border-border">
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-sm text-foreground">
                                    {address.substring(0, 10)}...{address.substring(address.length - 8)}
                                </span>
                                {riskScore !== undefined && (
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                        riskScore >= 0.8 ? 'bg-red-100 text-red-700' :
                                        riskScore >= 0.7 ? 'bg-orange-100 text-orange-700' :
                                        'bg-yellow-100 text-yellow-700'
                                    }`}>
                                        Risk: {(riskScore * 100).toFixed(0)}%
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Case Info */}
                    {caseName && (
                        <div className="bg-muted rounded-lg px-3 py-2 mb-3 border border-border">
                            <div className="flex items-center gap-2 text-foreground">
                                <FileText className="w-4 h-4" />
                                <span className="font-medium text-sm">{caseName}</span>
                            </div>
                        </div>
                    )}

                    {/* Action Button */}
                    <button
                        onClick={handleViewCase}
                        disabled={!caseId}
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ExternalLink className="w-4 h-4" />
                        View Case Details
                    </button>

                    {/* Info Text */}
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                        This address has been flagged based on risk analysis and saved for investigation.
                    </p>
                </div>

                {/* Progress Bar */}
                <div className="h-1 bg-muted">
                    <div className="h-full bg-primary animate-progress" style={{
                        animation: 'progress 8s linear forwards'
                    }} />
                </div>
            </div>

            <style>{`
                @keyframes progress {
                    from { width: 100%; }
                    to { width: 0%; }
                }
                .animate-progress {
                    width: 100%;
                }
            `}</style>
        </div>
    );
}
